import { Link } from '@tanstack/react-router'
import { Icon } from '@/components/icons'
import { BILL_LOCATION_LABELS, type BillLocationValue } from '@/constants/bill-location'
import type { Bill } from '@/models/bill/bill'
import { formatNumber } from '@/utils/number'

function paymentStatusLabel(status: string | undefined) {
  if (status === 'partial') return 'ชำระบางส่วน'
  return 'ยังไม่ชำระ'
}

function statusColor(status: string | undefined) {
  if (status === 'partial') return 'bg-amber-100 text-amber-900'
  return 'bg-rose-100 text-rose-900'
}

export function LiffOutstandingBillCard({ bill }: { bill: Bill }) {
  const remaining =
    bill.remaining_amount ?? bill.payment_information?.remaining_amount ?? 0
  const total = bill.total_amount ?? 0
  const status = bill.payment_information?.payment_status
  const loc = bill.location as BillLocationValue | undefined

  return (
    <li>
      <Link
        to="/liff/outstanding/$billId"
        params={{ billId: bill.id }}
        search={{
          tab: bill.bill_information?.bill_type === 'sell' ? 'sell' : 'buy',
        }}
        className="block rounded-2xl border border-border/50 bg-white p-4 shadow-sm transition active:scale-[0.99]"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-semibold text-slate-900">
              {bill.customer_information?.name ?? '—'}
            </p>
            <p className="mt-0.5 font-mono text-sm text-slate-500">
              {bill.bill_number ?? '—'}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${statusColor(status)}`}
          >
            {paymentStatusLabel(status)}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
          <span className="rounded-md bg-slate-100 px-2 py-1">
            {bill.bill_information?.bill_type === 'sell' ? 'ขาย' : 'ซื้อ'}
          </span>
          {loc ? (
            <span className="rounded-md bg-slate-100 px-2 py-1">
              {BILL_LOCATION_LABELS[loc] ?? loc}
            </span>
          ) : null}
          {bill.bill_date ? (
            <span className="rounded-md bg-slate-100 px-2 py-1">{bill.bill_date}</span>
          ) : null}
        </div>

        <div className="mt-3 flex items-end justify-between gap-2 border-t border-border/40 pt-3">
          <div>
            <p className="text-xs text-slate-500">ยอดบิล</p>
            <p className="text-sm font-medium text-slate-700">{formatNumber(total)} ฿</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-medium text-rose-700">ค้างชำระ</p>
            <p className="text-xl font-bold text-rose-600">{formatNumber(remaining)} ฿</p>
          </div>
          <Icon icon="lucide:chevron-right" className="size-6 shrink-0 text-slate-400" aria-hidden />
        </div>
      </Link>
    </li>
  )
}
